'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useSearch } from '@/hooks/useSearch';
import { Input } from '../ui/input';
import { Kbd } from '../ui/kbd';
import type { Pattern } from '@/lib/supabase';

interface SearchBarProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  showInstantResults?: boolean;
  className?: string;
}

function debounce<T extends unknown[]>(
  fn: (...args: T) => void,
  delay: number
): (...args: T) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: T) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function SearchBar({
  placeholder = 'Search patterns...',
  onSearch,
  showInstantResults = true,
  className = '',
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { results, isLoading, search, clear } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const topResults = results.slice(0, 5);
  const showDropdown = showInstantResults && isFocused && query.length >= 2;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      search(searchQuery);
      onSearch?.(searchQuery);
    }, 300),
    [search, onSearch]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      setSelectedIndex(-1);

      if (value.length >= 2) {
        debouncedSearch(value);
      } else {
        clear();
      }
    },
    [debouncedSearch, clear]
  );

  const handleClear = useCallback(() => {
    setQuery('');
    setSelectedIndex(-1);
    clear();
    inputRef.current?.focus();
  }, [clear]);

  const handleResultClick = useCallback(
    (pattern: Pattern) => {
      router.push(`/patterns/${pattern.slug}`);
      setIsFocused(false);
      setQuery('');
      clear();
    },
    [router, clear]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown && e.key === 'Escape') {
        handleClear();
        inputRef.current?.blur();
        return;
      }
      if (!showDropdown) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((p) => (p < topResults.length - 1 ? p + 1 : p));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((p) => (p > 0 ? p - 1 : -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleResultClick(topResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleClear();
        inputRef.current?.blur();
      }
    },
    [showDropdown, topResults, selectedIndex, handleClear, handleResultClick]
  );

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative w-full ${className}`} role="search">
      <div className="relative">
        <Input
          ref={inputRef}
          type="search"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          iconLeft={<Search className="h-4 w-4" aria-hidden="true" />}
          loading={isLoading}
          className="pr-24"
          aria-label="Search patterns"
          aria-autocomplete="list"
          aria-controls={showDropdown ? 'search-results' : undefined}
          aria-expanded={showDropdown}
        />
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
          {query && !isLoading && (
            <button onClick={handleClear} className="text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded" aria-label="Clear search">
              <X className="h-4 w-4" />
            </button>
          )}
          {!isFocused && !query && <Kbd keys={['cmd', 'K']} className="hidden sm:inline-flex" aria-label="Keyboard shortcut: Command K" />}
        </div>
      </div>

      <AnimatePresence>
        {showDropdown && topResults.length > 0 && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-2 w-full rounded-lg border border-gray-700 bg-gray-800 text-white shadow-lg"
            id="search-results"
            role="listbox"
            aria-label="Search results"
          >
            <div className="max-h-96 overflow-y-auto py-2">
              {topResults.map((pattern, index) => (
                <button
                  key={pattern.id}
                  onClick={() => handleResultClick(pattern)}
                  className={`w-full px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  role="option"
                  aria-selected={index === selectedIndex}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate font-medium text-gray-900">
                        {pattern.name}
                      </h4>
                      <p className="line-clamp-2 text-sm text-gray-600">
                        {pattern.description}
                      </p>
                    </div>
                    <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                      {pattern.category}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
